import { useCallback, useEffect, useState } from 'react';
import { cn, ButtonTabs } from '@divlab/divanui';

import LayoutMain from '@Layouts/LayoutMain';
import * as HeaderStore from '@Stores/Header';
import ServicePageWrapper from '@Components/ServicePageWrapper';
import SmartRender from '@Components/SmartRender';
import usePage from '@Queries/usePage';
import useTranslation from '@Queries/useTranslation';
import PageTopBar from '@Components/PageTopBar';
import useMedias from '@Hooks/useMedias/useMedias';
import { useScrollToView } from '@Hooks/useScrollToView';
import ListBlock from './elements/ListBlock';
import ConditionsToBuyInCredit from './elements/ConditionsToBuyInCredit';
import HowBuyInCredit from './elements/HowBuyInCredit';
import CardsPartners from './elements/CardsPartners';
import Tabs from './elements/Tabs';
import PartialPayments from './elements/PartialPayments';
import Page from '@Components/Page';
import InstallmentDetails from './elements/InstallmentDetails';
import styles from './PageCredit.module.css';

import type { PageCreditData } from './typings';
import type { FC, MouseEvent } from 'react';
import type { Tab } from '@divlab/divanui';

const PageCredit: FC = () => {
  const { data: page } = usePage<PageCreditData>();
  const { t } = useTranslation();
  const { isMobile } = useMedias();
  const [selectedBank, setSelectedBank] = useState<string>('');
  const { handleScrollTo } = useScrollToView();
  const { contentHeight } = HeaderStore.useSizes();
  const heading = page.meta.h1 || t('title');

  const handleChangeBank = useCallback(
    (e: MouseEvent, tab: Tab) => {
      setSelectedBank(tab.id);
      const element = document.getElementById(tab.id);
      handleScrollTo(element, contentHeight);
    },
    [contentHeight, handleScrollTo],
  );

  const handleChangeHash = useCallback(() => {
    if (!window.location.hash) return;

    const element = document.getElementById(window.location.hash.replace(/^#/, ''));

    if (!element) return;

    handleScrollTo(element, contentHeight);
  }, [contentHeight, handleScrollTo]);

  useEffect(() => {
    window.addEventListener('hashchange', handleChangeHash);

    return () => window.removeEventListener('hashchange', handleChangeHash);
  }, [handleChangeHash]);

  return (
    <LayoutMain>
      <Page>
        <PageTopBar title={heading} breadcrumbs={page.breadcrumbs} navigation={page.pageMenu} />
        <div className={styles.page} data-testid='page-credit'>
          <ServicePageWrapper>
            <div className={styles.topText}>{page.topText}</div>
            <SmartRender countries={['RUS']} condition={!!page.tabs}>
              <div className={styles.buttonTabsWrapper}>
                <ButtonTabs
                  scrollable={isMobile}
                  defaultValue={selectedBank}
                  tabs={page.tabs}
                  onChangeTab={handleChangeBank}
                />
              </div>
            </SmartRender>
          </ServicePageWrapper>

          {!!page.yandexSplit && <PartialPayments data={page.yandexSplit} />}

          <SmartRender countries={['RUS']} condition={!!page.directCredit}>
            <InstallmentDetails data={page.directCredit} />
          </SmartRender>

          {!!page.halvaPartial && <PartialPayments data={page.halvaPartial} />}

          {page.banners?.length > 0 && (
            <Tabs
              items={page.banners}
              className={cn(styles.bankBanner, { [styles.withButtons]: page.banners.length > 1 })}
            />
          )}

          <ServicePageWrapper>
            {!!page.installment && (
              <ListBlock className={styles.creditWithoutOverpayment} page={page.installment} />
            )}

            <SmartRender countries={['RUS', 'KAZ']} condition={!!page.advantages}>
              <ConditionsToBuyInCredit
                className={styles.conditionsToBuyInCredit}
                page={page.advantages}
              />
            </SmartRender>

            {page.howInstallment && (
              <HowBuyInCredit className={styles.howBuyInCredit} page={page.howInstallment} />
            )}
          </ServicePageWrapper>

          <SmartRender countries={['RUS']} condition={!!page.tinkoffInstallment}>
            <InstallmentDetails className={styles.tinkoff} data={page.tinkoffInstallment} />
          </SmartRender>

          <SmartRender countries={['RUS']} condition={!!page.halva}>
            <InstallmentDetails className={styles.halva} data={page.halva} />
          </SmartRender>

          <SmartRender countries={['BLR']} condition={!!page.partners}>
            <CardsPartners partners={page.partners} />
          </SmartRender>
        </div>
      </Page>
    </LayoutMain>
  );
};

export default PageCredit;
